import { useCallback, useState } from 'react';
import type { Location } from 'react-router-dom';
import type { NavItem } from './nav-items';

const INNSTILLINGER_PATH = '/innstillinger';

/**
 * Åpen/lukket-tilstand for sidebar-grupper. Innstillinger styres av AppLayout
 * (åpnes også av route-effekt og delt med mobilmenyen); øvrige grupper
 * (Salgsmuligheter) holder egen tilstand og er åpne når brukeren står under dem.
 */
export function useNavGroupState(
  location: Location,
  innstillingerOpen: boolean,
  setInnstillingerOpen: React.Dispatch<React.SetStateAction<boolean>>,
) {
  const [explicit, setExplicit] = useState<Record<string, boolean>>({});

  const isOpen = useCallback(
    (item: NavItem): boolean => {
      if (item.path === INNSTILLINGER_PATH) return innstillingerOpen;
      return explicit[item.path] ?? location.pathname.startsWith(item.path);
    },
    [explicit, innstillingerOpen, location.pathname],
  );

  const toggle = useCallback(
    (item: NavItem) => {
      if (item.path === INNSTILLINGER_PATH) {
        setInnstillingerOpen((o) => !o);
        return;
      }
      setExplicit((prev) => ({ ...prev, [item.path]: !(prev[item.path] ?? location.pathname.startsWith(item.path)) }));
    },
    [location.pathname, setInnstillingerOpen],
  );

  /** Lenkemål når sidebaren er kollapset: innstillinger har fast landing, andre går til første barn. */
  const collapsedTarget = useCallback((item: NavItem): string => {
    if (item.path === INNSTILLINGER_PATH) return '/innstillinger/organisasjon';
    return item.children?.[0]?.path ?? item.path;
  }, []);

  const open = useCallback(
    (item: NavItem) => {
      if (item.path === INNSTILLINGER_PATH) setInnstillingerOpen(true);
      else setExplicit((prev) => ({ ...prev, [item.path]: true }));
    },
    [setInnstillingerOpen],
  );

  return { isOpen, toggle, open, collapsedTarget };
}
